import {
    Component,
    ElementRef,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { getScrollParent } from '@cmps/cdk';
import {
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import {
    animationFrameScheduler,
    BehaviorSubject,
    Observable,
    of,
    Subject,
} from 'rxjs';
import {
    auditTime,
    catchError,
    retryWhen,
    scan,
    take,
    tap,
} from 'rxjs/operators';

import { AuthService } from '../../auth/auth.service';
import { GoalStatus } from '../../coaching-base/general';
import { OpportunityView } from '../../coaching-base/opportunity';
import {
    CoachingSwitchboardService,
    SwitchboardPillarTwoComm,
} from '../../coaching-base/switchboard';
import {
    scrollIntoViewAuto,
    ScrollIntoViewContext,
    scrollIntoViewInstant,
} from '../../coaching-observation/bring-into-focus.service';
import {
    composeTagIdentifierNext,
    FocusIdentifier,
    FocusShallow,
} from '../../coaching-tags/tags/tags.model';
import { actions, addPageAction } from '../../coaching/new-relic';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { StateToProps } from '../../store';
import { useDispatcher, UseStore } from '../../store.decorator';
import {
    navigateToExistingCommitment,
    navigateToExistingDraftCommitment,
    navigateToExistingObservation,
    navigateToNewObservation,
} from '../navigate';
import { UserView } from '../pillar-one';
import {
    watchForNumberParamChange,
    watchForSubOutletChange,
    watchForUserChange,
} from '../watch-route';

import { CommitmentCategory } from './commitment-category.model';
import { CommitmentSummaryStatusView } from './commitment-summary-status-view';
import { CommitmentSummaryView } from './commitment-summary-view.model';
import { ObservationCategory } from './observation-category.model';
import {
    ObservationSummaryContainerView,
    ObservationSummaryView,
} from './observation-summary.model';
import {
    reload,
    reloadCancel,
    selectMetric,
    setActive,
    setCategoryOpenState,
    setCategoryOpenStateForObservations,
    setStatuses,
} from './pillar-two.action';
import { getCategoryAndId } from './pillar-two.function';
import { TranslationKeyMap } from './pillar-two.i18n';
import { coachingDetailPillarTwoReducer } from './pillar-two.reducer';
import { PillarTwoState } from './pillar-two.state';
import { StatusView } from './status-view.model';
import { TabRefId } from './tab-ref-id.model';

interface SwitchboardCallback {
    matchesTab?: (
        prevState: PillarTwoState,
        checkCommitments: boolean,
        checkObservations: boolean,
    ) => boolean;
    resolve?: () => void;
    reject?: () => void;
}

function createSwitchboardCallback(): {
    cb: SwitchboardCallback;
    prom: Promise<void>;
} {
    const cb: SwitchboardCallback = {};
    const prom = new Promise<void>((resolve, reject) => {
        cb.resolve = resolve;
        cb.reject = reject;
    });
    return { cb, prom };
}

@Component({
    selector: 'cmps-coaching-detail-pillar-two',
    templateUrl: './pillar-two.component.html',
    host: {
        class: 'cmps-coaching-detail-pillar-two',
    },
})
@TakeUntilDestroy()
@UseStore<PillarTwoState>(
    'coachingDetail.pillarTwo',
    coachingDetailPillarTwoReducer,
)
export class CoachingDetailPillarTwoComponent
    implements
        OnInit,
        OnDestroy,
        StateToProps<PillarTwoState>,
        SwitchboardPillarTwoComm
{
    state: PillarTwoState;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _TabRefId = TabRefId;
    /** @private */ _commitmentSummaryStatusView = CommitmentSummaryStatusView;
    /** @private */ _opportunity: OpportunityView;

    @Input() pillarOneCollapsible: boolean;
    /** @private */ _selectedTabRefId: TabRefId;
    private _scrollIntoViewAuto: Subject<ScrollIntoViewContext>;
    private readonly _readySubj: BehaviorSubject<boolean>;
    private readonly _commitmentSubj: BehaviorSubject<CommitmentSummaryView>;
    private readonly _observationsSubj: BehaviorSubject<
        ObservationSummaryContainerView[]
    >;

    private readonly _reloadCallbacks: SwitchboardCallback[];
    private readonly _setStatusesCallbacks: SwitchboardCallback[];
    /** @private */ _userObs: Observable<UserView>;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _router: Router,
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _translateService: TranslateService,
        private readonly _dialog: CmpsDialog,
        private readonly _switchboardService: CoachingSwitchboardService,
        private readonly _authService: AuthService,
    ) {
        // TODO revise if this or have subscribers be responsible for last emitted
        this._readySubj = new BehaviorSubject(false);
        this._commitmentSubj = new BehaviorSubject(null);
        this._observationsSubj = new BehaviorSubject(null);

        this._reloadCallbacks = [];
        this._setStatusesCallbacks = [];
    }

    ngOnInit(): void {
        watchForNumberParamChange(
            this._activatedRoute,
            'metricId',
            this.state.metricId,
            false,
        )
            .pipe(
                tap((metricId) => {
                    useDispatcher(this, selectMetric({ metricId }));
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        watchForUserChange(this._activatedRoute)
            .pipe(
                tap((nextUserId) => {
                    const which =
                        this.state.location.which || TabRefId.COMMITMENTS;
                    const tabs: TabRefId[] = [
                        which,
                        which === TabRefId.COMMITMENTS
                            ? TabRefId.OBSERVATIONS
                            : TabRefId.COMMITMENTS,
                    ];
                    tabs.forEach((e) => {
                        useDispatcher(
                            this,
                            reload({
                                userId: nextUserId,
                                authUserId: this._authService.user.id,
                                which: e,
                            }),
                        );
                    });
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        watchForSubOutletChange(this._router)
            .pipe(
                tap(([which, id]) => {
                    const next =
                        which === 'observation'
                            ? TabRefId.OBSERVATIONS
                            : TabRefId.COMMITMENTS;
                    useDispatcher(this, setActive({ which: next, id }));

                    // TODO see todo in _onCommitmentClick()
                    if (which === 'observation' && id === 'new') {
                        useDispatcher(
                            this,
                            setCategoryOpenStateForObservations({
                                cmtIdentifier: null,
                            }),
                        );
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        const oppObs =
            this._switchboardService.pillarOne.sbSelectedOpportunity();
        if (oppObs) {
            oppObs
                .pipe(
                    tap((opp) => {
                        this._opportunity = opp;
                    }),
                    takeUntilDestroy(this),
                )
                .subscribe();
        }

        const { subject, observable } = scrollIntoViewAuto(() => {
            const el1 = this._el.nativeElement.querySelector(
                '.cmps-coaching-detail-pillar-two--content-wrapper > div:not([hidden])',
            );
            if (!el1) {
                return null;
            }
            return el1.querySelector(
                '.cmps-coaching-detail-pillar-two--accordion',
            ).parentElement.parentElement;
        });
        this._scrollIntoViewAuto = subject;
        observable.pipe(takeUntilDestroy(this)).subscribe();

        this._switchboardService.pillarTwo.connect(this);

        this._userObs = this._switchboardService.pillarOne.sbSelectedUser();
    }

    ngOnDestroy(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._reloadCallbacks.forEach((e) => e.reject && e.reject());
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._setStatusesCallbacks.forEach((e) => e.reject && e.reject());
        useDispatcher(this, reloadCancel({}));
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._scrollIntoViewAuto && this._scrollIntoViewAuto.complete();
        this._switchboardService.pillarTwo.disconnect();
        this._readySubj.complete();
        this._commitmentSubj.complete();
        this._observationsSubj.complete();
    }

    stateToProps(prevState?: PillarTwoState): void {
        if (
            (prevState.pending.reload && !this.state.pending.reload) ||
            (prevState.pending.reloadCommitments &&
                !this.state.pending.reloadCommitments) ||
            (prevState.pending.reloadObservations &&
                !this.state.pending.reloadObservations)
        ) {
            // only reloadCommitments for the moment
            this._readySubj.next(!this.state.pending.reloadCommitments);

            for (const cb of this._reloadCallbacks) {
                if (
                    !cb.matchesTab ||
                    cb.matchesTab(
                        prevState,
                        !this.state.pending.reloadCommitments,
                        !this.state.pending.reloadObservations,
                    )
                ) {
                    this._reloadCallbacks.splice(
                        this._reloadCallbacks.indexOf(cb),
                        1,
                    );
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    cb.resolve && cb.resolve();
                }
            }

            let onError: () => void;

            this._scrollIntoView(
                `[data-${this.state.location.which}='${this.state.location.id}']`,
                null,
                onError,
            );
        }

        if (prevState.location.which !== this.state.location.which) {
            this._selectedTabRefId = this.state.location.which;
        }
        if (
            prevState.commitmentStatuses !== this.state.commitmentStatuses ||
            prevState.observationStatuses !== this.state.observationStatuses
        ) {
            for (const cb of this._setStatusesCallbacks) {
                this._setStatusesCallbacks.splice(
                    this._setStatusesCallbacks.indexOf(cb),
                    1,
                );
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                cb.resolve && cb.resolve();
            }
        }

        if (
            prevState.commitmentCategories !== this.state.commitmentCategories
        ) {
            this._commitmentSubj.next(
                this.sbSelectedOpportunityActiveCommitmentSnapshot(),
            );
        }

        if (
            prevState.observationCategories !== this.state.observationCategories
        ) {
            this._observationsSubj.next(this.sbOrderedObservationsSnapshot());
        }
    }

    propsToDispatch(): void {
        this._commitmentSubj.next(
            this.sbSelectedOpportunityActiveCommitmentSnapshot(),
        );
        this._observationsSubj.next(this.sbOrderedObservationsSnapshot());
    }

    sbReady(): Observable<boolean> {
        return this._readySubj.asObservable();
    }

    sbSelectedOpportunityHasActiveCommitment(): boolean {
        return this.sbSelectedOpportunityActiveCommitmentSnapshot() != null;
    }

    sbSelectedOpportunityActiveCommitment(): Observable<CommitmentSummaryView> {
        return this._commitmentSubj.asObservable();
    }

    /** first active commitment with selected opportunity */
    sbSelectedOpportunityActiveCommitmentSnapshot(): CommitmentSummaryView {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this.state &&
            this.state.commitmentCategories &&
            this.state.commitmentCategories.length &&
            this.state.commitmentCategories[1].items
        ) {
            return this.state.commitmentCategories[1].items.find((e) => {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                return (
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    e.goals &&
                    e.goals.some((ee) => ee.metricId === this.state.metricId)
                );
            });
        }
        return undefined;
    }

    sbSwitchTab(which: TabRefId): void {
        this._selectedTabRefId = which;
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    sbReload(which: TabRefId): Promise<void> {
        const tabs: TabRefId[] =
            which == null
                ? [null]
                : [
                      which,
                      which === TabRefId.COMMITMENTS
                          ? TabRefId.OBSERVATIONS
                          : TabRefId.COMMITMENTS,
                  ];

        // reload each separately, but only return the callback for the tab requested
        const { cb, prom } = createSwitchboardCallback();
        cb.matchesTab = (
            prevState: PillarTwoState,
            checkCommitments: boolean,
            checkObservations: boolean,
        ) => {
            if (
                (which == null || which === TabRefId.COMMITMENTS) &&
                checkCommitments
            ) {
                return (
                    this.state.commitmentCategories !==
                    prevState.commitmentCategories
                );
            }
            if (
                (which == null || which === TabRefId.OBSERVATIONS) &&
                checkObservations
            ) {
                return (
                    this.state.observationCategories !==
                    prevState.observationCategories
                );
            }
            return false;
        };
        this._reloadCallbacks.push(cb);

        tabs.forEach((e) => {
            useDispatcher(
                this,
                reload({
                    // eslint-disable-next-line @typescript-eslint/dot-notation
                    userId: this._activatedRoute.snapshot.params['userId'],
                    authUserId: this._authService.user.id,
                    which: e,
                    retainIsOpen: true,
                }),
            );
        });

        this._switchboardService.transcriptSummary.sbReload();

        return prom;
    }

    sbSetActive(which: TabRefId, id: number | string): void {
        useDispatcher(this, setActive({ which, id }));
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    sbSetStatuses(statuses: StatusView[]): Promise<void> {
        const { cb, prom } = createSwitchboardCallback();
        this._setStatusesCallbacks.push(cb);
        useDispatcher(this, setStatuses({ statuses }));
        return prom;
    }

    sbScrollIntoView(
        which: TabRefId,
        id: number | string,
        identifier?: FocusIdentifier,
        method?: 'instant' | 'smooth',
    ): void {
        this._selectedTabRefId = which;

        const { category, id: itemId } = getCategoryAndId(
            this.state.commitmentCategories,
            this.state.observationCategories,
            which,
            id,
            identifier,
        );
        if (category && !category.isOpen) {
            // no callback cause this is synchronous
            useDispatcher(
                this,
                setCategoryOpenState({ category, isOpen: true }),
            );
        }

        let onError: () => void;

        this._scrollIntoView(`[data-${which}='${itemId}']`, method, onError);
    }

    sbOrderedObservations(): Observable<ObservationSummaryContainerView[]> {
        return this._observationsSubj.asObservable();
    }

    sbOrderedObservationsSnapshot(): ObservationSummaryContainerView[] {
        if (!this.state || !this.state.observationCategories) {
            return null;
        }
        const ret: ObservationSummaryContainerView[] = [];
        this.state.observationCategories.forEach((e) => {
            if (e.behaviour != null) {
                Object.keys(e.focusItems).forEach((key) => {
                    ret.push(...e.focusItems[key].focusContainers);
                });
            } else {
                ret.push(...e.focusItems.draftItems.focusContainers);
            }
        });
        return ret;
    }

    sbActiveCommitmentWithFocus(focus: FocusShallow): CommitmentSummaryView {
        try {
            const check = (e: CommitmentSummaryView): boolean => {
                const active =
                    e.statusId === CommitmentSummaryStatusView.ACTIVE ||
                    e.statusId === CommitmentSummaryStatusView.OTHER_ACTIVE;
                const focusMatches =
                    e.behaviourId === focus.behaviourId &&
                    e.subBehaviourId === focus.subBehaviourId;
                return active && focusMatches;
            };
            for (const cat of this.state.commitmentCategories) {
                for (const e of cat.items) {
                    if (check(e)) {
                        return e;
                    }
                }
            }
            return null;
        } catch {
            return undefined;
        }
    }

    sbHasActiveCommitmentWithFocus(focus: FocusShallow): boolean {
        return this.sbActiveCommitmentWithFocus(focus) != null;
    }

    sbHasActiveCommitmentWithGoal(
        selectedMetricIds: number[],
    ): Record<number, boolean> {
        const result: Record<number, boolean> = {};

        try {
            const check = (
                e: CommitmentSummaryView,
                metricId: number,
            ): boolean => {
                const active =
                    e.statusId === CommitmentSummaryStatusView.ACTIVE ||
                    e.statusId === CommitmentSummaryStatusView.OTHER_ACTIVE;

                const metricMatch = e.goals.some(
                    (goal) =>
                        metricId === goal.metricId &&
                        goal.statusId === GoalStatus.ACTIVE,
                );

                return active && metricMatch;
            };

            selectedMetricIds.forEach((metricId) => {
                result[metricId] = false;

                for (const cat of this.state.commitmentCategories) {
                    for (const e of cat.items) {
                        if (check(e, metricId)) {
                            result[metricId] = true;
                            break;
                        }
                    }
                    if (result[metricId]) {
                        break;
                    }
                }
            });

            return result;
        } catch {
            return result;
        }
    }

    /** @private */
    _onCommitmentClick(item: CommitmentSummaryView): void {
        addPageAction(actions.PillarTwo.CMT_CLICK, {
            draftId: item?.draftId,
            commitmentId: item?.commitmentId,
            behaviourId: item?.behaviourId,
            subBehaviourId: item?.subBehaviourId,
        });

        // TODO move to tap of watchForSubOutletChange()
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        const cmtIdentifier = `${item.behaviourId}-${item.subBehaviourId}-${!!(
            item.draftId || item.commitmentId
        )}`;
        useDispatcher(
            this,
            setCategoryOpenStateForObservations({ cmtIdentifier }),
        );

        this._goToCommitment(item, undefined);
    }

    /** @private */
    _onEditCommitmentClick(item: CommitmentSummaryView): void {
        const pageActionAttrs = {
            draftId: item?.draftId,
            commitmentId: item?.commitmentId,
            behaviourId: item?.behaviourId,
            subBehaviourId: item?.subBehaviourId,
        };
        addPageAction(actions.PillarTwo.EDIT_CMT_CLICK, pageActionAttrs);

        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._9580_DO_YOU_WANT_TO_EDIT_THIS_COMMITMENT_,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9297_YES,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9298_NO,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret === 0) {
                        addPageAction(
                            actions.PillarTwo.EDIT_CMT_YES_CLICK,
                            pageActionAttrs,
                        );
                    }
                    if (ret === 1) {
                        addPageAction(
                            actions.PillarTwo.EDIT_CMT_NO_CLICK,
                            pageActionAttrs,
                        );
                    }
                    if (ret == null) {
                        addPageAction(
                            actions.PillarTwo.EDIT_CMT_DISMISS,
                            pageActionAttrs,
                        );
                    }

                    if (ret !== 0) {
                        return;
                    }
                    this._goToCommitment(item, true);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    _onAddObservationClick(item: CommitmentSummaryView): void {
        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._9581_DO_YOU_WANT_TO_ADD_AN_OBSERVATION_TO_THIS_COMMITMENT_,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9297_YES,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9298_NO,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret === 0) {
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        navigateToNewObservation(
                            this._router,
                            this._activatedRoute,
                            undefined,
                            item.draftId,
                            item.commitmentId,
                            composeTagIdentifierNext(item),
                        );
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    _onObservationClick(
        item: ObservationSummaryView,
        fc: ObservationSummaryContainerView,
    ): void {
        this._gotoObservation(fc.observationId, item.published);
    }

    /** @private */
    _onCategoryOpenChange(
        category: CommitmentCategory | ObservationCategory,
        isOpen: boolean,
    ): void {
        useDispatcher(this, setCategoryOpenState({ category, isOpen }));
    }

    private _goToCommitment(item: CommitmentSummaryView, edit: boolean): void {
        if (
            !item.isOriginalCommitment &&
            (item.draftId || item.targetedDraftId)
        ) {
            let approvalId: number;
            const statusIds = [
                CommitmentSummaryStatusView.DRAFT, // api will serve disputed drafts
                CommitmentSummaryStatusView.PRE_APPROVAL,
                CommitmentSummaryStatusView.OTHER_PRE_APPROVAL,
                CommitmentSummaryStatusView.UPDATING,
                CommitmentSummaryStatusView.OTHER_UPDATING,
                CommitmentSummaryStatusView.PENDING_CLOSE,
                CommitmentSummaryStatusView.OTHER_PENDING_CLOSE,
            ];
            if (statusIds.includes(item.statusId)) {
                /* this was set to be targetedApprovalId. i.e. non-null on the receiver side.
                now apparently it's for both sender and receiver should load the approval accordingly */
                approvalId = item.approvalId
                    ? item.approvalId
                    : item.targetedApprovalId;
            }

            // disputed drafts always open in edit mode on the sender side
            let edit_: boolean;
            if (item.approved !== true) {
                edit_ = true;
            }

            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            navigateToExistingDraftCommitment(
                this._router,
                this._activatedRoute,
                item.draftId || item.targetedDraftId,
                approvalId,
                item.commitmentId,
                undefined,
                undefined,
                undefined,
                edit_ || edit,
            );
        } else {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            navigateToExistingCommitment(
                this._router,
                this._activatedRoute,
                item.commitmentId,
                undefined,
                undefined,
                undefined,
                edit,
            );
        }
    }

    private _gotoObservation(observationId: number, published: boolean): void {
        if (published) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            navigateToExistingObservation(
                this._router,
                this._activatedRoute,
                observationId,
            );
        } else {
            const data: CmpsConfirmDialogOptions = {
                title: this._translateService.instant(
                    TranslationKeyMap._9479_CONFIRMATION,
                ),
                message: this._translateService.instant(
                    TranslationKeyMap._9583_DO_YOU_WANT_TO_EDIT_THIS_OBSERVATION_,
                ),
                actions: [
                    {
                        label: this._translateService.instant(
                            TranslationKeyMap._9297_YES,
                        ),
                        data: 0,
                    },
                    {
                        label: this._translateService.instant(
                            TranslationKeyMap._9298_NO,
                        ),
                        data: 1,
                    },
                ],
            };
            const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
            ref.result
                .pipe(
                    tap((ret) => {
                        if (ret === 0) {
                            // eslint-disable-next-line @typescript-eslint/no-floating-promises
                            navigateToExistingObservation(
                                this._router,
                                this._activatedRoute,
                                observationId,
                            );
                        }
                    }),
                    takeUntilDestroy(this),
                )
                .subscribe();
        }
    }

    // abstract this out of the component later
    private _scrollIntoView(
        selector: string,
        method?: 'instant' | 'smooth',
        onError?: () => void,
    ): void {
        let cancelError = false;
        this._ngZone.onStable
            .pipe(
                take(1),
                tap(() => {
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                    const el = this._el.nativeElement.querySelector(
                        selector,
                    ) as HTMLElement;
                    if (!el) {
                        throw new Error('not found');
                    }
                    cancelError = true;
                    if (method === 'smooth') {
                        this._scrollIntoViewAuto.next({
                            element: el,
                            callback: null,
                        });
                    } else {
                        let sel = getScrollParent(el, true);
                        if (!sel) {
                            throw new Error('not found');
                        }
                        sel = getScrollParent(sel, true);
                        if (!sel) {
                            throw new Error('not found');
                        }
                        scrollIntoViewInstant(el, sel);
                    }
                }),
                retryWhen((errors) =>
                    errors.pipe(
                        scan((acc) => {
                            if (acc >= 5) {
                                throw new Error('still not found');
                            }
                            return acc + 1;
                        }, 0),
                        auditTime(0, animationFrameScheduler),
                    ),
                ),
                catchError(() => {
                    !cancelError && onError && onError();
                    return of(null);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
